import React, { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import PublicFooter from '@/components/PublicFooter';

export default function CustomerLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  // NOTE: All hooks MUST be declared before any conditional early return.
  // Previously the `if (user) return <Navigate>` short-circuit was placed
  // BEFORE `useCallback`, which violated the Rules of Hooks and triggered
  // React concurrent-rendering crashes after a successful login.
  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError(null);
      const r = await login(email.trim().toLowerCase(), password);
      if (r.ok) navigate('/customer', { replace: true });
      else setError(r.error || 'Login failed');
    },
    [email, password, login, navigate],
  );

  if (user && user.role === 'customer') {
    return <Navigate to="/customer" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-violet-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center shadow-lg">
            <Sparkles className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-slate-900 text-2xl font-bold mt-3">BeautyMateUK</h1>
          <p className="text-slate-500 text-sm">Welcome back</p>
        </div>

        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="text-center space-y-1">
            <CardTitle className="text-xl">Sign in</CardTitle>
            <CardDescription>Continue booking beauty pros at home</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" data-testid="customer-login-email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-password">Password</Label>
                <Input id="c-password" type="password" data-testid="customer-login-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" required />
              </div>
              {error && (
                <div data-testid="customer-login-error" className="text-sm rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2">{error}</div>
              )}
              <Button type="submit" disabled={loading} data-testid="customer-login-submit" className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-base">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...</> : 'Sign in'}
              </Button>
              <p className="text-center text-sm text-slate-600 pt-2">
                New here?{' '}
                <button type="button" onClick={() => navigate('/customer/signup')} className="text-rose-600 font-medium hover:underline" data-testid="to-customer-signup">
                  Create account
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
        <PublicFooter variant="minimal" />
      </div>
    </div>
  );
}
